// ============================================================================
// DECLARATION ENGINE V2.0 — Motor de Declarações
// Layout: sidebar 360px + painel A4 central + painel direito (xl)
// Padrão idêntico ao ProcurationEngine / ContractEngine
// ============================================================================

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  Download,
  ChevronDown,
  CheckCircle,
  Edit3,
  Trash2,
  X,
  EyeOff,
  Plus,
} from "lucide-react";
import {
  DocumentShell,
  ConfigSection,
} from "../../components/layout/DocumentShell";
import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { db, auth } from "../../shared/api/firebase/config";
import { FIREBASE_CONFIG } from "../../config/Config";
import {
  loadDeclarationTemplate,
  subscribeDeclarationTemplate,
  updateDocumentStructure,
  findClientWorkspace,
  findExistingDeclaration,
  saveDeclaration,
  publishDeclaration,
  deleteDeclarationPublication,
  saveDeclarationTemplate,
} from "../../services/DeclarationEngineRepository.js";
import { CrmClientPickerModal } from "../../components/CrmClientPickerModal";
import { fetchClientData } from "../../services/ProcurationEngineRepository";
import { CadernoSelector } from "../../components/ui/CadernoSelector";
import { useOfficeData } from "../settings/useOfficeData";
import {
  DECLARATION_TYPES,
  HIPOSSUFICIENCIA_REASONS,
  DECLARATION_STRUCTURE,
  buildPlaceholderVars,
  resolveText,
  isDeclarationBlockVisible,
  isDeclarationChildVisible,
} from "./DeclarationData";
import {
  buildSignatureLine,
  hydrateAndBuildCanonicalClientQualification,
} from "../../config/ClientSchema";
import {
  DocumentHeader,
  DocumentFooter,
} from "../../components/layout/DocumentLetterhead";
import { SaveCancelBar } from "../../components/ui/SaveCancelBar";
import { toTitleCasePt } from "../../utils/formatters";
import headerSvgRaw from "../../assets/letterhead/header.svg?raw";
import footerSvgRaw from "../../assets/letterhead/Papel Timbrado-2025 - fontes corretas - rodap\u00e9 para pdf - para contrato do site.svg?raw";
import "../../styles/legal-print.css";
import { fetchWithWarmup } from "../../utils/backendWarmup";

const appId = FIREBASE_CONFIG.projectId;
const storage = getStorage();
const PUBLISHING_API_BASE_URL = String(
  import.meta?.env?.VITE_BACKEND_URL || "https://rda-backend-thuq.onrender.com",
)
  .trim()
  .replace(/\/+$/, "");
const AUTOSAVE_DELAY_MS = 8000;
const DRAFT_KEY = "rda_declaration_draft_v1";

// Numeração hierárquica por nível (mesma lógica do ProcurationEngine —
// computeHierNumbers): mantém um contador por nível numa pilha, resetando os
// níveis mais profundos quando um nível mais raso avança. Produz "4.1.",
// "4.1.1.", "4.1.2.", "4.2." em vez de um contador plano "4.1./4.2./4.3./4.4.".
const normalizeClauseLevel = (value) => {
  let lvl = Number(value || 0);
  if (!Number.isFinite(lvl) || lvl < 0) lvl = 0;
  return Math.floor(lvl);
};

const computeHierNumbers = (children, macroNum) => {
  const out = [];
  const stack = [];
  const arr = Array.isArray(children) ? children : [];
  const macro = typeof macroNum === "number" ? macroNum : null;

  for (let i = 0; i < arr.length; i += 1) {
    const child = arr[i] || {};
    const lvl = normalizeClauseLevel(child.level);

    while (stack.length <= lvl) stack.push(0);
    stack.length = lvl + 1;
    stack[lvl] = (stack[lvl] || 0) + 1;
    for (let k = 0; k < lvl; k += 1) {
      if (!stack[k]) stack[k] = 1;
    }

    const parts = [];
    if (macro !== null) parts.push(String(macro));
    for (let k = 0; k <= lvl; k += 1) parts.push(String(stack[k] || 1));
    out.push(parts.join(".") + ".");
  }

  return out;
};

const labelBaseStyle =
  "text-[10px] font-bold text-gray-500 uppercase tracking-widest";
const inputBaseStyle =
  "w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[12px] font-medium text-gray-800 outline-none transition-all focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]";

const RAMOS = [
  "Cível",
  "Criminal / Penal",
  "Trabalhista",
  "Tributário / Fiscal",
  "Previdenciário",
  "Família",
  "Ambiental",
  "Consumidor",
  "Administrativo",
  "Outro",
];

// Structure is now sourced from DECLARATION_STRUCTURE (imported) or Firestore template

// ── Main Engine ───────────────────────────────────────────────────────────────

export function DeclarationFactory() {
  const { data: officeData } = useOfficeData();

  // ── State ─────────────────────────────────────────────────────────────────
  const [openSection, setOpenSection] = useState("client");
  const [mode, setMode] = useState("producao");
  const [showImport, setShowImport] = useState(false);
  const [showAdvancedActions, setShowAdvancedActions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [cloudSyncState, setCloudSyncState] = useState("idle");
  const [error, setError] = useState("");
  const [toastMsg, setToastMsg] = useState("");

  const [isModified, setIsModified] = useState(false);
  const [cloudSyncMeta, setCloudSyncMeta] = useState({
    updatedAt: null,
    updatedBy: null,
    source: null,
    error: null,
  });
  const [dialogState, setDialogState] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm",
    onConfirm: null,
    onCancel: null,
  });

  const closeDialog = () => {
    const fn = dialogState.onCancel;
    setDialogState({
      open: false,
      title: "",
      message: "",
      mode: "confirm",
      onConfirm: null,
      onCancel: null,
    });
    if (typeof fn === "function") fn();
  };

  // Form data
  const [clientData, setClientData] = useState(null);
  const [declType, setDeclType] = useState("hipossuficiencia");
  const [reasonCodes, setReasonCodes] = useState([]);
  const [ramoDireito] = useState("Hipossuficiência");
  const [observacoes, setObservacoes] = useState("");
  const [clauseOverrides, setClauseOverrides] = useState({});

  // Published doc tracking (same pattern as ProcurationEngine)
  const [editingPublicationMeta, setEditingPublicationMeta] = useState(null);
  const [cadernoId, setCadernoId] = useState("");
  const [cadernoTitle, setCadernoTitle] = useState("");

  // ── Structure state (Firestore ou fallback local DECLARATION_STRUCTURE) ────
  const [structure, setStructure] = useState(DECLARATION_STRUCTURE);
  const [editingBlockId, setEditingBlockId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const advancedMenuRef = useRef(null);
  const advancedBtnRef = useRef(null);
  const draftPromptedRef = useRef(false);
  const [advancedMenuPos, setAdvancedMenuPos] = useState({ top: 0, left: 0 });

  // ── Placeholder vars (recalcula quando clientData ou officeData muda) ──────
  const placeholderVars = useMemo(
    () => buildPlaceholderVars({ clientData, officeData }),
    [clientData, officeData],
  );

  // ── Derived: clauses only (blocks that are type=clause) ────────────────────
  const clauseBlocks = useMemo(
    () => structure.filter((b) => b.type === "clause"),
    [structure],
  );

  // ── Carregar template do Firestore em TEMPO REAL (v2) ─────────────────────
  // Espelha subscribeProcurationMasterV1 (onSnapshot) — mudanças na nuvem
  // refletem automaticamente na tela, sem refresh manual.
  useEffect(() => {
    const validate = (data) =>
      data && Array.isArray(data.structure) ? data : null;
    const applyTemplate = (tplData) => {
      if (validate(tplData)) {
        setStructure(tplData.structure);
        console.log(
          "[DeclarationEngine] Template Firestore v2 carregado: tpl_hipossuficiencia_v2",
        );
      } else if (tplData === null) {
        console.log(
          "[DeclarationEngine] Template v2 não encontrado no Firestore, usando DECLARATION_STRUCTURE local.",
        );
      }
    };

    // Carrega snapshot único inicial (fallback se o listener falhar)
    loadDeclarationTemplate()
      .then(applyTemplate)
      .catch((err) =>
        console.warn(
          "[DeclarationEngine] Erro ao carregar template do Firestore, usando fallback local:",
          err?.message,
        ),
      );

    // Assina atualizações em tempo real (igual procuracao master_v1)
    const unsubscribe = subscribeDeclarationTemplate(applyTemplate);
    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clientName = String(
    clientData?.qualification?.physical?.name || clientData?.name || "",
  ).trim();
  const clientId = String(clientData?.id || "").trim();
  const clientCpf = String(
    clientData?.qualification?.physical?.cpf || "",
  ).trim();
  const clientRg = String(clientData?.qualification?.physical?.rg || "").trim();

  // ── Signature / gender helpers (precisa estar ANTES do handlePublishPdf) ──
  const sig = clientData ? buildSignatureLine(clientData) : null;
  const signerName = sig?.nameLine || "[Declarante não informado]";
  const signerCpf = (sig?.cpfLine || "CPF: [Não informado]").replace(
    /^CPF:\s*/i,
    "CPF ",
  );
  const signerSub = String(sig?.subtitle || "").trim();
  const _decGender = String(
    clientData?.grammaticalGender ||
      clientData?.qualification?.physical?.grammaticalGender ||
      "",
  )
    .trim()
    .toUpperCase();
  const decArtigoUC = _decGender === "F" ? "A" : "O";
  const decArtigo = _decGender === "F" ? "a" : "o";
  const decPelo = _decGender === "F" ? "pela" : "pelo";

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3500);
  }, []);

  // ── Debounced autosave (igual ao ProcurationEngine / ContractEngine) ─────
  useEffect(() => {
    if (!isModified) return;
    const docId = String(editingPublicationMeta?.documentId || "").trim();
    if (!docId) return;
    setCloudSyncState("saving");
    const timer = setTimeout(async () => {
      try {
        const actorName = String(
          auth?.currentUser?.displayName ||
            auth?.currentUser?.email ||
            "Operador",
        ).trim();
        const now = new Date().toISOString();
        await updateDocumentStructure(
          docId,
          structure,
          reasonCodes,
          clauseOverrides,
          ramoDireito,
          actorName,
        );
        setIsModified(false);
        setCloudSyncState("saved");
        setCloudSyncMeta({
          updatedAt: now,
          updatedBy: actorName,
          source: "cloud_autosave",
          error: null,
        });
      } catch (e) {
        setCloudSyncState("error");
        setCloudSyncMeta((prev) => ({
          ...prev,
          error: e?.message || "Erro no autosave",
        }));
      }
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [
    isModified,
    structure,
    reasonCodes,
    clauseOverrides,
    ramoDireito,
    observacoes,
    editingPublicationMeta,
  ]);

  // ── Draft detection on mount (igual ao ProcurationEngine) ───────────────────
  useEffect(() => {
    if (draftPromptedRef.current) return;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (!saved) return;
      const draft = JSON.parse(saved);
      if (!draft?.clientData) return;
      draftPromptedRef.current = true;
      setDialogState({
        open: true,
        title: "Rascunho Encontrado",
        message:
          'Detectamos uma declaração que estava sendo editada anteriormente.\n\nDeseja restaurar este rascunho para continuar o trabalho?\n\nSe clicar em "Cancelar", você iniciará um documento em branco limpo.',
        mode: "confirm",
        onConfirm: () => {
          try {
            setClientData(draft.clientData);
            if (draft.declType) setDeclType(draft.declType);
            if (Array.isArray(draft.reasonCodes))
              setReasonCodes(draft.reasonCodes);
            if (draft.observacoes !== undefined)
              setObservacoes(draft.observacoes);
            if (draft.clauseOverrides)
              setClauseOverrides(draft.clauseOverrides);
            if (draft.cadernoId) setCadernoId(draft.cadernoId);
            if (draft.cadernoTitle) setCadernoTitle(draft.cadernoTitle);
            setIsModified(true);
          } catch {
            /* ignore */
          }
        },
        onCancel: () => {
          try {
            localStorage.removeItem(DRAFT_KEY);
          } catch {
            /* ignore */
          }
        },
      });
    } catch {
      /* ignore */
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Draft auto-save to localStorage ─────────────────────────────────────────
  useEffect(() => {
    if (!isModified || !clientData) return;
    try {
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({
          clientData,
          declType,
          reasonCodes,
          observacoes,
          clauseOverrides,
          cadernoId,
          cadernoTitle,
        }),
      );
    } catch {
      /* ignore */
    }
  }, [
    isModified,
    clientData,
    declType,
    reasonCodes,
    observacoes,
    clauseOverrides,
    cadernoId,
    cadernoTitle,
  ]);

  // ── Advanced menu position ────────────────────────────────────────────────
  const openAdvancedMenu = () => {
    if (advancedBtnRef.current) {
      const rect = advancedBtnRef.current.getBoundingClientRect();
      setAdvancedMenuPos({ top: rect.top, left: rect.left });
    }
    setShowAdvancedActions(true);
  };

  useEffect(() => {
    if (!showAdvancedActions) return;
    const onDown = (e) => {
      if (
        advancedMenuRef.current &&
        !advancedMenuRef.current.contains(e.target) &&
        advancedBtnRef.current &&
        !advancedBtnRef.current.contains(e.target)
      ) {
        setShowAdvancedActions(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [showAdvancedActions]);

  // ── Handle client import ──────────────────────────────────────────────────
  const handleImportClient = useCallback(async (picked) => {
    if (!picked) return;
    setClientData(picked);
    setShowImport(false);
    setOpenSection("hipossuficiencia");
    setIsModified(true);
    setCloudSyncState("idle");
    const pickedId = String(picked?.id || picked?.clientId || "").trim();
    if (!pickedId) {
      setEditingPublicationMeta(null);
      setCadernoId("");
      setCadernoTitle("");
      return;
    }
    // Auto-detectar caderno do cliente
    try {
      const firstWs = await findClientWorkspace(pickedId);
      if (firstWs) {
        setCadernoId(firstWs.id);
        setCadernoTitle(String(firstWs.data()?.title || "").trim());
      } else {
        setCadernoId("");
        setCadernoTitle("");
      }
    } catch (_wsErr) {
      console.warn("[DeclarationEngine] Auto-detect caderno falhou:", _wsErr);
      setCadernoId("");
      setCadernoTitle("");
    }
    // Auto-detectar declaração existente do cliente (evita duplicatas)
    try {
      const latest = await findExistingDeclaration(pickedId);
      if (latest) {
        setEditingPublicationMeta({
          documentId: latest.id,
          publicationVersion: Number(latest.data?.publicationVersion || 0),
        });
        console.log(
          "[DeclarationEngine] Declaração existente reutilizada:",
          latest.id,
        );
      } else {
        setEditingPublicationMeta(null);
      }
    } catch (_declErr) {
      console.warn(
        "[DeclarationEngine] Auto-detect declaração falhou:",
        _declErr,
      );
      setEditingPublicationMeta(null);
    }
  }, []);

  // ── Salvar HTML ───────────────────────────────────────────────────────────
  const handleSaveHtml = useCallback(async () => {
    if (!clientData) {
      setError("Importe um cliente do CRM antes de salvar.");
      return;
    }
    if (!cadernoId) {
      setError(
        "Selecione o Caderno de Autos (Dados do Processo) antes de salvar. A declaração precisa estar vinculada a um processo.",
      );
      return;
    }
    if (isSaving) return;
    setIsSaving(true);
    setError("");
    setCloudSyncState("saving");
    try {
      const actorName = String(
        auth?.currentUser?.displayName ||
          auth?.currentUser?.email ||
          "Operador",
      ).trim();
      const title = `Declaração de Hipossuficiência — ${toTitleCasePt(clientName)}`;
      const existingId = String(
        editingPublicationMeta?.documentId || "",
      ).trim();
      const now = new Date().toISOString();
      const payload = {
        type: "DECLARATION",
        subtype: declType,
        title,
        name: title,
        clientId,
        clientVisible: false,
        status: "PUBLISHED",
        structure: JSON.parse(JSON.stringify(structure)),
        reasonCodes,
        clauseOverrides,
        ramoDireito,
        observacoes,
        category: "FORMAL_DOCUMENT",
        classification: { category: "declaracao" },
        module: "declarations",
        requiresSignature: true,
        requiresConsent: false,
        consentStatus: "not_required",
        publication: {
          source: "declaration_engine",
          format: "html",
          publishedAtIso: now,
          publishedBy: actorName,
          storageMode: "html_archive",
        },
        cadernoId: cadernoId || null,
        cadernoTitle: cadernoTitle || null,
        createdBy: auth?.currentUser?.email || null,
        updatedBy: auth?.currentUser?.email || null,
      };

      const docId = await saveDeclaration(payload, clientId, existingId);

      setEditingPublicationMeta({ documentId: docId, publicationVersion: 0 });
      setIsModified(false);
      setCloudSyncState("saved");
      setCloudSyncMeta({
        updatedAt: now,
        updatedBy: actorName,
        source: "html_save",
        error: null,
      });
      showToast(
        "Declaração salva no prontuário. NÃO visível ao cliente ainda. Publique o PDF para liberar.",
      );
    } catch (err) {
      console.error("[Declarations] Save HTML failed:", err);
      setError(`Erro ao salvar: ${err?.message || "Desconhecido"}`);
      setCloudSyncState("error");
      setCloudSyncMeta((prev) => ({
        ...prev,
        error: err?.message || "Erro desconhecido",
      }));
    } finally {
      setIsSaving(false);
    }
  }, [
    clientData,
    structure,
    reasonCodes,
    clauseOverrides,
    declType,
    ramoDireito,
    observacoes,
    clientId,
    clientName,
    editingPublicationMeta,
    isSaving,
    showToast,
    cadernoId,
    cadernoTitle,
  ]);

  // ── Publicar PDF ──────────────────────────────────────────────────────────
  const handlePublishPdf = useCallback(async () => {
    if (!editingPublicationMeta?.documentId) {
      setError("Salve o HTML primeiro antes de publicar o PDF.");
      return;
    }
    if (isPublishing) return;
    setIsPublishing(true);
    setError("");
    setCloudSyncState("saving");
    try {
      // ── FRESH MASTER FETCH: Garantir que o template mais recente está no DOM ──
      // A Declaração já mantém `structure` sincronizado via onSnapshot em
      // tempo real, mas esse listener pode ficar temporariamente pausado
      // durante edição ativa (mesmo risco documentado em ContractEngine.jsx
      // e ProcurationEngine.jsx). Busca explícita aqui como defesa extra
      // antes de capturar o snapshot que vira o PDF do cliente — mesmo
      // padrão dos outros 2 motores (Art. 357/§10.1). Pulado se houver
      // edição local não salva, para não sobrescrevê-la.
      if (!isModified) {
        try {
          const freshData = await loadDeclarationTemplate();
          if (freshData?.structure && Array.isArray(freshData.structure)) {
            setStructure(freshData.structure);
            await new Promise((resolve) => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  setTimeout(resolve, 800);
                });
              });
            });
            console.log(
              "[DeclarationEngine] Fresh master aplicado ao DOM antes da captura.",
            );
          }
        } catch (_freshErr) {
          console.warn(
            "[DeclarationEngine] Fresh master fetch falhou (non-blocking):",
            _freshErr?.message,
          );
        }
      }

      const docId = String(editingPublicationMeta.documentId).trim();
      const safeCadernoId = String(cadernoId || "").trim();
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const stem = `[${ts}]-Declaracao-Hipossuficiencia-${clientName.replace(/\s+/g, "-").toUpperCase()}`;
      const fileName = `${stem}.pdf`;
      const storagePath = `artifacts/${appId}/public/documents/declarations/${clientId}/${fileName}`;

      let fileUrl = "";
      let downloadUrl = "";
      let pdfBlob = null;
      try {
        const token = await getToken(true);
        if (token) {
          // ── Motor IDÊNTICO ao ProcurationEngine.buildPublicationHtml ──
          const escapeHtml = (v) =>
            String(v || "")
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")
              .replace(/"/g, "&quot;")
              .replace(/'/g, "&#39;");
          const normalizeAbsoluteUrl = (raw) => {
            const value = String(raw || "").trim();
            if (!value) return "";
            try {
              return new URL(value, window.location.origin).href;
            } catch {
              return value;
            }
          };

          const paperSource = (
            centerPanelRef.current || document
          ).querySelector(".contract-print-area");
          const paperClone = paperSource ? paperSource.cloneNode(true) : null;

          if (
            paperClone &&
            !paperClone.querySelector("#contrato-legal-pronto")
          ) {
            const readyAnchor = document.createElement("div");
            readyAnchor.id = "contrato-legal-pronto";
            readyAnchor.setAttribute("data-status", "ready");
            readyAnchor.style.display = "none";
            paperClone.appendChild(readyAnchor);
          }

          if (paperClone) {
            paperClone.querySelectorAll("[src]").forEach((node) => {
              try {
                const current = node.getAttribute("src");
                if (current)
                  node.setAttribute("src", normalizeAbsoluteUrl(current));
              } catch {}
            });
            paperClone.querySelectorAll("[href]").forEach((node) => {
              try {
                const current = node.getAttribute("href");
                if (current && !String(current).startsWith("#"))
                  node.setAttribute("href", normalizeAbsoluteUrl(current));
              } catch {}
            });
          }

          const styleTags = Array.from(document.querySelectorAll("style"))
            .map((node) => `<style>${String(node?.textContent || "")}</style>`)
            .join("\n");

          const linkTags = Array.from(
            document.querySelectorAll('link[rel="stylesheet"]'),
          )
            .map((node) => {
              const href = normalizeAbsoluteUrl(node?.getAttribute("href"));
              return href
                ? `<link rel="stylesheet" href="${escapeHtml(href)}" />`
                : "";
            })
            .filter(Boolean)
            .join("\n");

          const declTitle = `Declaração de Hipossuficiência — ${toTitleCasePt(clientName)}`;
          const bodyHtml =
            paperClone?.outerHTML ||
            `<div class="published-paper-fallback"><h1>${escapeHtml(declTitle)}</h1><p>Snapshot visual indisponível no momento da publicação.</p></div>`;
          const actorLabel =
            String(displayName || email || "Operador").trim() || "Operador";

          const fullHtml = `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(declTitle)}</title>
  ${linkTags}
  ${styleTags}
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap">
  <style>
    html, body { background: #f3f4f6; margin: 0; padding: 0; }
    body { font-family: 'Roboto', sans-serif; -webkit-font-smoothing: antialiased; color: #20243B; }
    .published-shell { max-width: 1120px; margin: 0 auto; padding: 24px; }
    .publication-banner { margin-bottom: 16px; border: 1px solid #dbe2ea; background: #fff; border-radius: 16px; padding: 16px 18px; }
    .publication-banner h1 { margin: 0 0 8px 0; font-size: 18px; font-weight: 800; }
    .publication-banner p { margin: 4px 0; font-size: 12px; line-height: 1.6; }
    .publication-banner ul { margin: 8px 0 0 20px; padding: 0; font-size: 12px; }
    .screen-paper { margin: 0 auto; }
    .no-print { display: none !important; }
    .quadro-resumo-icon { display: none !important; }
    .ghost-marker { display: none !important; }
    @media print {
      html, body { background: white !important; }
      .published-shell { max-width: none !important; margin: 0 !important; padding: 0 !important; }
      .publication-banner { display: none !important; }
      .contract-print-area, .print-only-view, .print-layout-table, .document-content { margin-top: 0 !important; }
      #guia-rapido-print, #guia-rapido { margin-top: 0 !important; }
    }
  </style>
</head>
<body>
  <div class="published-shell">
    <div class="publication-banner">
      <h1>${escapeHtml(declTitle)}</h1>
      <p>Documento publicado no sistema interno do Portal do Cliente em ${escapeHtml(new Date().toLocaleString("pt-BR"))} por ${escapeHtml(actorLabel)}.</p>
    </div>
    ${bodyHtml}
  </div>
</body>
</html>`;
          const resp = await fetchWithWarmup(
            `${PUBLISHING_API_BASE_URL}/drive/generate-pdf?t=${Date.now()}`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ html: fullHtml, fileName }),
            },
            {
              onWaking: () =>
                showToast(
                  "⏳ Backend acordando... aguarde até 40s na primeira vez.",
                ),
              onRetry: (attempt, max) =>
                showToast(`🔄 Tentativa ${attempt + 1}/${max}... aguarde.`),
            },
          );
          if (resp.ok) {
            pdfBlob = await resp.blob();
            const sRef = storageRef(storage, storagePath);
            await uploadBytes(sRef, pdfBlob, {
              contentType: "application/pdf",
            });
            downloadUrl = await getDownloadURL(sRef);
            fileUrl = downloadUrl;
          }
        }
      } catch (pdfErr) {
        console.warn(
          "[Declarations] PDF via backend failed (non-blocking):",
          pdfErr?.message,
        );
      }

      // Declaração NÃO faz upload para Drive — PDF fica APENAS no Firebase Storage.
      // Isso evita PDFs fantasma aparecendo na lista de arquivos do caderno.

      const currentVersion =
        Number(editingPublicationMeta?.publicationVersion || 0) + 1;
      const now = new Date().toISOString();
      const actorName = String(
        auth?.currentUser?.displayName ||
          auth?.currentUser?.email ||
          "Operador",
      ).trim();
      await publishDeclaration(docId, {
        status: "PUBLISHED",
        fileUrl,
        downloadUrl,
        storagePath: fileUrl ? storagePath : "",
        fileName,
        mimeType: "application/pdf",
        extension: "pdf",
        publicationVersion: currentVersion,
        clientVisible: true,
        cadernoId: safeCadernoId || null,
        cadernoTitle: cadernoTitle || null,
        publication: {
          source: "declaration_engine",
          format: "pdf",
          publishedAtIso: now,
          publishedBy: actorName,
          storageMode: fileUrl ? "pdf_archive" : "html_archive",
        },
        updatedBy: auth?.currentUser?.email || null,
      });

      // Declaração NÃO cria slot no organizer — aparece apenas no banner dourado (formalDocs)

      setEditingPublicationMeta((prev) => ({
        ...prev,
        publicationVersion: currentVersion,
        cadernoId: safeCadernoId || null,
        cadernoTitle: cadernoTitle || null,
      }));
      setIsModified(false);
      setCloudSyncState("saved");
      setCloudSyncMeta({
        updatedAt: now,
        updatedBy: actorName,
        source: "pdf_publish",
        error: null,
      });
      showToast(
        fileUrl
          ? "✅ Declaração publicada em PDF — visível no portal do cliente para assinar!"
          : "⚠️ Declaração publicada (HTML). Backend PDF indisponível — verifique o Render.",
      );
    } catch (err) {
      console.error("[Declarations] Publish PDF failed:", err);
      setError(`Erro ao publicar PDF: ${err?.message || "Desconhecido"}`);
      setCloudSyncState("error");
      setCloudSyncMeta((prev) => ({ ...prev, error: err?.message || "Erro" }));
    } finally {
      setIsPublishing(false);
    }
  }, [
    editingPublicationMeta,
    isPublishing,
    clientName,
    clientId,
    cadernoId,
    cadernoTitle,
    showToast,
    sig,
    signerName,
    signerCpf,
    signerSub,
    decArtigoUC,
  ]);

  // ── Imprimir / PDF local ───────────────────────────────────────────────────
  // Nome de arquivo amigável (o navegador usa document.title como sugestão de
  // nome ao salvar em PDF) — mesmo padrão do ProcurationEngine/handlePrint.
  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    const nextTitle = clientName
      ? `Declaracao - ${clientName}`
      : "Declaracao de Hipossuficiencia";
    const restoreTitle = () => {
      document.title = previousTitle;
      window.removeEventListener("afterprint", restoreTitle);
    };

    document.title = nextTitle;
    window.addEventListener("afterprint", restoreTitle);
    window.print();
    window.setTimeout(restoreTitle, 1000);
  }, [clientName]);

  // ── Reset ─────────────────────────────────────────────────────────────────
  // ── Backup Seguro (JSON) — espelha handleDownloadBackup da Procuração ──────
  const handleBackupJson = () => {
    try {
      if (!Array.isArray(structure) || structure.length === 0) {
        alert("A declaração está vazia ou ainda carregando. Nada para fazer backup.");
        return;
      }
      const payload = JSON.stringify(
        {
          declType,
          reasonCodes,
          observacoes,
          clauseOverrides,
          clientData,
          structure,
        },
        null,
        2,
      );
      const blob = new Blob([payload], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().split("T")[0];
      const a = document.createElement("a");
      a.href = url;
      a.download = `RDA_Declaracao_Backup_${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDialogState({
        open: true,
        title: "Backup Concluído",
        message: "✅ SUCESSO!\n\nUma cópia de segurança da declaração foi baixada para o seu computador (pasta Downloads).",
        mode: "info",
        onConfirm: null,
      });
    } catch (e) {
      console.error("Erro ao gerar backup:", e);
      alert("Erro ao tentar gerar o arquivo de backup.");
    }
  };

  // ── Restaurar Backup (JSON) ───────────────────────────────────────────────
  const handleRestoreBackup = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const restored = JSON.parse(ev.target.result);
          if (restored.structure) setStructure(restored.structure);
          if (restored.declType) setDeclType(restored.declType);
          if (Array.isArray(restored.reasonCodes)) setReasonCodes(restored.reasonCodes);
          if (restored.observacoes !== undefined) setObservacoes(restored.observacoes);
          if (restored.clauseOverrides) setClauseOverrides(restored.clauseOverrides);
          if (restored.clientData) setClientData(restored.clientData);
          setIsModified(true);
          setDialogState({
            open: true,
            title: "Restauração Concluída",
            message: "✅ SUCESSO!\n\nA declaração foi restaurada a partir do backup.",
            mode: "info",
            onConfirm: null,
          });
        } catch (err) {
          console.error("Erro ao restaurar backup:", err);
          alert("Erro ao ler o arquivo de backup. Verifique se é um JSON válido.");
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  // ── Baixar para Revisão (.MD) ────────────────────────────────────────────
  const handleExportMD = () => {
    if (!Array.isArray(structure) || structure.length === 0) {
      alert("Nenhuma estrutura carregada.");
      return;
    }
    const htmlToMarkdown = (html) => {
      let text = String(html || "");
      text = text.replace(/<strong>(.*?)<\/strong>/gi, "**$1**");
      text = text.replace(/<br\s*[\/]?>/gi, "\n");
      text = text.replace(/<\/?[^>]*>/g, "");
      text = text.replace(/&nbsp;/g, " ");
      return text.trim();
    };
    let md = "# DECLARAÇÃO — REVISÃO COMPLETA (RDA SYSTEM)\n\n";
    md += `> **Data da exportação:** ${new Date().toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}\n\n`;
    md += `## FICHA RESUMO\n`;
    md += `| Campo | Valor |\n|---|---|\n`;
    md += `| **Declarante** | ${clientName || "(não informado)"} |\n`;
    md += `| **CPF** | ${clientCpf || "(não informado)"} |\n`;
    md += `| **Tipo** | ${declType || "hipossuficiencia"} |\n`;
    md += `\n---\n\n`;
    structure.forEach((block) => {
      const visible = isDeclarationBlockVisible(block, reasonCodes);
      if (!visible) return;
      const blockTitle = resolveText(block.title || "", placeholderVars);
      md += `## ${blockTitle}\n`;
      if (block.type === "text") {
        const resolved = resolveText(block.content || "", placeholderVars);
        const mdText = htmlToMarkdown(resolved);
        if (mdText) md += `${mdText}\n\n`;
      } else {
        const children = Array.isArray(block.children) ? block.children : [];
        children.forEach((child) => {
          if (!isDeclarationChildVisible(child, reasonCodes)) return;
          const childMd = htmlToMarkdown(resolveText(child.content || "", placeholderVars));
          if (childMd) md += `- ${childMd}\n\n`;
        });
      }
      md += `---\n\n`;
    });
    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const dateStr = new Date().toISOString().split("T")[0];
    const a = document.createElement("a");
    a.href = url;
    a.download = `Revisao_Declaracao_RDA_${dateStr}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── Upload da Revisão (.MD) ──────────────────────────────────────────────
  const handleImportMD = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".md,text/markdown";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const md = String(ev.target.result || "");
          const blocksById = new Map();
          (Array.isArray(structure) ? structure : []).forEach((b) => {
            if (b) blocksById.set(String(b.id), b);
          });
          const lines = md.replace(/\r\n/g, "\n").split("\n");
          let currentBlockId = null;
          const buffer = [];
          const flush = () => {
            const raw = buffer.join("\n").trim();
            buffer.length = 0;
            if (!raw) return;
            if (!currentBlockId || !blocksById.has(currentBlockId)) return;
            const b = blocksById.get(currentBlockId);
            if (b) b.content = raw;
          };
          for (const line of lines) {
            const blockMatch = line.match(/^##\s+(.+)$/);
            if (blockMatch) {
              flush();
              const title = blockMatch[1].trim();
              const found = Array.from(blocksById.values()).find(
                (bb) => String(resolveText(bb.title || "", placeholderVars)).toLowerCase() === title.toLowerCase(),
              );
              currentBlockId = found ? found.id : null;
              continue;
            }
            buffer.push(line);
          }
          flush();
          setStructure([...structure]);
          setIsModified(true);
          setDialogState({
            open: true,
            title: "Importação Concluída",
            message: "✅ SUCESSO!\n\nA revisão (.MD) foi importada e aplicada à declaração que está na tela.",
            mode: "info",
            onConfirm: null,
          });
        } catch (err) {
          console.error("Erro ao importar revisão (.MD):", err);
          alert("Erro ao importar revisão (.MD): " + (err?.message || String(err)));
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };
  // ── Revincular do CRM (adaptado: clientData, não parties) ──────────────────
  const handleRevincularCrm = () => {
    setDialogState({
      open: true,
      title: "Revincular do CRM",
      message:
        "Esta ação não apaga dados do cadastro. O sistema irá recarregar do CRM o cliente vinculado por clientId e substituir snapshots antigos pela qualificação canônica atual. Deseja continuar?",
      mode: "confirm",
      onConfirm: async () => {
        const linkedId = String(clientData?.id || clientData?.clientId || "").trim();
        if (!linkedId) {
          setDialogState({
            open: true,
            title: "Sem cliente vinculado",
            message: "Nenhum cliente vinculado por ID no CRM. Importe o declarante primeiro.",
            mode: "info",
            onConfirm: null,
          });
          return;
        }
        try {
          const raw = await fetchClientData(linkedId);
          if (!raw) {
            setDialogState({
              open: true,
              title: "Cliente não encontrado",
              message: `Nenhum cliente com ID ${linkedId} encontrado no CRM.`,
              mode: "info",
              onConfirm: null,
            });
            return;
          }
          let hydrated = raw;
          try {
            const canonical = await hydrateAndBuildCanonicalClientQualification(raw);
            hydrated = canonical?.client || raw;
          } catch {
            hydrated = raw;
          }
          setClientData(hydrated);
          setIsModified(true);
          setDialogState({
            open: true,
            title: "Revinculação concluída",
            message: "✅ Cliente revinculado do CRM com a qualificação canônica atual.",
            mode: "info",
            onConfirm: null,
          });
        } catch (e) {
          setDialogState({
            open: true,
            title: "Erro ao revincular",
            message: "Erro: " + (e?.message || String(e)),
            mode: "info",
            onConfirm: null,
          });
        }
      },
    });
  };

  // ── ☁️ Sincronizar com a Nuvem (template) ─────────────────────────────────
  const handleSyncCloud = async () => {
    try {
      const data = await loadDeclarationTemplate();
      if (!data || !Array.isArray(data.structure) || data.structure.length === 0) {
        throw new Error("Template da nuvem não encontrado ou vazio.");
      }
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch { /* ignore */ }
      setStructure(data.structure);
      setIsModified(false);
      setCloudSyncState("saved");
      setDialogState({
        open: true,
        title: "Sincronização concluída",
        message: `✅ Template sincronizado com a nuvem!\n\n${data.structure.length} blocos carregados do tpl_hipossuficiencia_v2.\nCache local limpo com sucesso.`,
        mode: "info",
        onConfirm: null,
      });
    } catch (e) {
      setDialogState({
        open: true,
        title: "Falha na sincronização",
        message: String(e?.message || e || "Não foi possível sincronizar com a nuvem."),
        mode: "alert",
        onConfirm: null,
      });
    }
  };

  // ── Baixar Mestre da Nuvem (JSON) ────────────────────────────────────────
  const handleDownloadCloudMaster = async () => {
    try {
      const data = await loadDeclarationTemplate();
      if (!data || !Array.isArray(data.structure) || data.structure.length === 0) {
        throw new Error("Nenhum mestre de declaração encontrado na nuvem.");
      }
      const payload = JSON.stringify(
        {
          source: "cloud_master",
          downloadedAt: new Date().toISOString(),
          updatedBy: data?.updatedBy || null,
          updatedAt: data?.updatedAt || null,
          structure: data.structure,
        },
        null,
        2,
      );
      const blob = new Blob([payload], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().split("T")[0];
      const a = document.createElement("a");
      a.href = url;
      a.download = `RDA_Declaracao_CloudMaster_${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDialogState({
        open: true,
        title: "Cloud Master Baixado",
        message: "✅ SUCESSO!\n\nO mestre da nuvem foi baixado como JSON para inspeção local, sem sobrescrever nada no sistema.",
        mode: "info",
        onConfirm: null,
      });
    } catch (e) {
      setDialogState({
        open: true,
        title: "Erro ao Baixar Mestre da Nuvem",
        message: "Erro: " + (e?.message || String(e)),
        mode: "info",
        onConfirm: null,
      });
    }
  };

  // ── Publicar Mestre (Admin / Lázaro) ─────────────────────────────────────
  const handleSaveAsMaster = () => {
    const message =
      "☢️ OPERAÇÃO LÁZARO (ADMIN) ☢️\n\n" +
      "ATENÇÃO: Esta ação vai sobrescrever o Mestre de Declarações na nuvem (tpl_hipossuficiencia_v2) com o que está na tela.\n\n" +
      "Depois, o sistema pode ser resetado com segurança para voltar ao mestre.\n\n" +
      "Continuar?";
    setDialogState({
      open: true,
      title: "Publicar Mestre (Admin)",
      message,
      mode: "confirm",
      onConfirm: async () => {
        setIsSaving(true);
        try {
          if (!Array.isArray(structure) || structure.length === 0) {
            throw new Error("Estrutura vazia.");
          }
          await saveDeclarationTemplate({
            structure,
            updatedBy: auth?.currentUser?.email || null,
          });
          setDialogState({
            open: true,
            title: "Sucesso",
            message: "✅ SUCESSO! O Mestre de Declarações foi publicado na nuvem.",
            mode: "info",
            onConfirm: null,
          });
        } catch (e) {
          setDialogState({
            open: true,
            title: "Erro ao Publicar Mestre",
            message: "Erro: " + (e?.message || String(e)),
            mode: "info",
            onConfirm: null,
          });
        } finally {
          setIsSaving(false);
        }
      },
    });
  };
  const handleReset = useCallback(() => {
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {
      /* ignore */
    }
    setClientData(null);
    setEditingPublicationMeta(null);
    setCadernoId("");
    setCadernoTitle("");
    setOpenSection("client");
    setObservacoes("");
    setError("");
    setIsModified(false);
    setCloudSyncState("idle");
    setCloudSyncMeta({
      updatedAt: null,
      updatedBy: null,
      source: null,
      error: null,
    });
    setReasonCodes([]);
    setClauseOverrides({});
    setDeclType("hipossuficiencia");
  }, []);

  const centerPanelRef = useRef(null);

  const scrollToBlock = useCallback((blockId) => {
    const el = document.getElementById(`decl-block-${blockId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // ── renderBlock: renderiza cada bloco da structure (paradigma modular) ──────
  const renderBlock = (block, clauseIndex, { forPrint = false } = {}) => {
    const isVisible = isDeclarationBlockVisible(block, reasonCodes);
    const showInMode = mode === "auditoria" || isVisible;
    if (!showInMode && !forPrint) return null;
    if (!isVisible && forPrint) return null;

    const blockTitle = resolveText(block.title, placeholderVars);
    const handleTopoClick = (e) => {
      e.preventDefault();
      const el = document.getElementById("guia-rapido");
      if (el && centerPanelRef.current)
        centerPanelRef.current.scrollTo({
          top: el.offsetTop - 60,
          behavior: "smooth",
        });
    };
    const isEditing = editingBlockId === block.id;
    const overrideKey = block.id;
    const hasOverride = clauseOverrides[overrideKey] !== undefined;

    // ── type: text (preâmbulo) ──
    if (block.type === "text") {
      const resolvedContent = resolveText(block.content || "", placeholderVars);
      return (
        <div
          key={block.id}
          id={block.id}
          className="clause-block mb-2"
          style={{ borderLeft: "none", paddingLeft: "0.5rem" }}
        >
          <h2 className="clause-heading preamble-heading relative">
            <span className="clause-heading-row w-full flex items-start flex-wrap gap-1">
              <span className="clause-title">PREÂMBULO</span>
              <span className="ml-auto flex items-center gap-1 relative z-10">
                {!forPrint && mode === "auditoria" && (
                  <button
                    onClick={() => {
                      setEditingBlockId(block.id);
                      setEditDraft({
                        content:
                          clauseOverrides[overrideKey] || block.content || "",
                      });
                    }}
                    className="text-[#20243B] hover:bg-blue-50 p-1 rounded no-print"
                    type="button"
                    title="Editar"
                  >
                    <Edit3 className="w-3 h-3" />
                  </button>
                )}
                <a
                  href={forPrint ? "#guia-rapido-print" : "#guia-rapido"}
                  className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 px-2 py-1 rounded hover:bg-gray-200 transition-colors no-underline relative inline-block overflow-hidden"
                  title="Voltar ao topo"
                  onClick={forPrint ? undefined : handleTopoClick}
                >
                  TOPO<span aria-hidden="true"> ↑</span>
                </a>
              </span>
            </span>
          </h2>
          {isEditing && !forPrint ? (
            <div className="bg-white border border-[#C5A059]/40 rounded-xl shadow-lg overflow-hidden no-print mt-2">
              <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#20243B] text-white">
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/80">
                  Editando Preâmbulo
                </div>
                <button
                  onClick={() => setEditingBlockId(null)}
                  className="text-white/70 hover:text-white"
                  type="button"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-4">
                <textarea
                  className="w-full h-[300px] text-[9px] font-mono border border-gray-300 rounded-lg p-2 resize-none outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/30 bg-white"
                  value={editDraft?.content || ""}
                  onChange={(e) => setEditDraft({ content: e.target.value })}
                  spellCheck={false}
                />
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => {
                      if (editDraft?.content?.trim()) {
                        setClauseOverrides((p) => ({
                          ...p,
                          [overrideKey]: editDraft.content.trim(),
                        }));
                      } else {
                        setClauseOverrides((p) => {
                          const n = { ...p };
                          delete n[overrideKey];
                          return n;
                        });
                      }
                      setEditingBlockId(null);
                      setIsModified(true);
                    }}
                    className="px-4 py-2 text-[11px] font-bold text-white rounded-lg"
                    style={{ background: "#20243B" }}
                    type="button"
                  >
                    Salvar
                  </button>
                  <button
                    onClick={() => setEditingBlockId(null)}
                    className="px-4 py-2 text-[11px] font-bold border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100"
                    type="button"
                  >
                    Cancelar
                  </button>
                  {hasOverride && (
                    <button
                      onClick={() => {
                        setClauseOverrides((p) => {
                          const n = { ...p };
                          delete n[overrideKey];
                          return n;
                        });
                        setEditingBlockId(null);
                        setIsModified(true);
                      }}
                      className="px-4 py-2 text-[11px] font-bold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 ml-auto"
                      type="button"
                    >
                      Restaurar Padrão
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div
              className="contract-p clause-body"
              dangerouslySetInnerHTML={{
                __html: hasOverride
                  ? resolveText(clauseOverrides[overrideKey], placeholderVars)
                  : resolvedContent,
              }}
            />
          )}
        </div>
      );
    }

    // ── type: clause ──
    const visibleChildren = (block.children || []).filter((ch) =>
      forPrint
        ? isDeclarationChildVisible(ch, reasonCodes)
        : mode === "auditoria" || isDeclarationChildVisible(ch, reasonCodes),
    );

    // Compute sub-clause numbering (hierárquico por nível — computeHierNumbers)
    const hierNums = computeHierNumbers(visibleChildren, clauseIndex);
    const numberedChildren = visibleChildren.map((ch, idx) => {
      const childVisible = isDeclarationChildVisible(ch, reasonCodes);
      if (childVisible || mode === "auditoria") {
        return { ...ch, displayNum: hierNums[idx] };
      }
      return { ...ch, displayNum: "" };
    });

    return (
      <div
        key={block.id}
        id={forPrint ? `block-${block.id}` : `decl-block-${block.id}`}
        className={`clause-block group relative transition-all duration-200 ${!isVisible ? "opacity-50 border-l-2 border-dashed border-gray-300" : ""} hover:bg-blue-50/30 rounded-lg`}
        style={{
          borderLeft: isVisible ? "none" : undefined,
          paddingLeft: "0.5rem",
        }}
      >
        <h2 className="clause-heading relative">
          <span className="clause-heading-row w-full flex items-start flex-wrap gap-1">
            <span className="clause-number">Cláusula {clauseIndex}ª –</span>
            <span className="clause-title">{blockTitle}</span>
            {!isVisible && mode === "auditoria" && (
              <EyeOff className="w-3.5 h-3.5 text-gray-400 ml-1" />
            )}
            <span className="ml-auto flex items-center gap-1 relative z-10">
              {!forPrint && mode === "auditoria" && (
                <>
                  <button
                    onClick={() => {
                      setEditingBlockId(block.id);
                      setEditDraft({
                        children: JSON.parse(
                          JSON.stringify(block.children || []),
                        ),
                      });
                    }}
                    className="text-[#20243B] hover:bg-blue-50 p-1 rounded no-print"
                    type="button"
                    title="Editar"
                  >
                    <Edit3 className="w-3 h-3" />
                  </button>
                  {hasOverride && (
                    <button
                      onClick={() => {
                        setClauseOverrides((p) => {
                          const n = { ...p };
                          delete n[overrideKey];
                          return n;
                        });
                        setIsModified(true);
                      }}
                      className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded no-print"
                      type="button"
                      title="Restaurar padrão"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </>
              )}
              <a
                href={forPrint ? "#guia-rapido-print" : "#guia-rapido"}
                className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 px-2 py-1 rounded hover:bg-gray-200 transition-colors no-underline relative inline-block overflow-hidden"
                title="Voltar ao topo"
                onClick={forPrint ? undefined : handleTopoClick}
              >
                TOPO<span aria-hidden="true"> ↑</span>
              </a>
            </span>
          </span>
        </h2>

        {isEditing && !forPrint ? (
          <div className="bg-white border border-[#C5A059]/40 rounded-xl shadow-lg overflow-hidden no-print mt-2">
            <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#20243B] text-white">
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/80">
                Editando Cláusula {clauseIndex}ª — {blockTitle}
              </div>
              <button
                onClick={() => setEditingBlockId(null)}
                className="text-white/70 hover:text-white"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-2 max-h-[500px] overflow-y-auto">
              {(editDraft?.children || []).map((ch, ci) => (
                <div
                  key={ch.id || ci}
                  className="border border-gray-200 rounded-lg p-2 space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-bold text-gray-500">
                      {clauseIndex}.{ci + 1}.
                    </span>
                    <span className="text-[8px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono">
                      {ch.condition || "ALWAYS"}
                    </span>
                    <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                      L{ch.level || 0}
                    </span>
                    <button
                      onClick={() => {
                        const next = [...editDraft.children];
                        next.splice(ci, 1);
                        setEditDraft({ children: next });
                      }}
                      className="ml-auto text-red-400 hover:text-red-600 p-0.5"
                      type="button"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  <textarea
                    className="w-full text-[9px] font-mono border border-gray-200 rounded p-1.5 min-h-[60px] resize-y outline-none focus:border-[#C5A059]"
                    value={ch.content || ""}
                    onChange={(e) => {
                      const next = [...editDraft.children];
                      next[ci] = { ...next[ci], content: e.target.value };
                      setEditDraft({ children: next });
                    }}
                    spellCheck={false}
                  />
                </div>
              ))}
              <button
                onClick={() => {
                  const newId = `${block.id}_new_${Date.now()}`;
                  setEditDraft({
                    children: [
                      ...(editDraft?.children || []),
                      {
                        id: newId,
                        type: "sub_clause",
                        content: "",
                        condition: "ALWAYS",
                        level: 0,
                      },
                    ],
                  });
                }}
                className="w-full py-2 text-[10px] font-bold text-indigo-700 border border-dashed border-indigo-300 rounded-lg hover:bg-indigo-50"
                type="button"
              >
                <Plus className="w-3 h-3 inline mr-1" />
                Adicionar Sub-cláusula
              </button>
              <div className="mt-3 pt-3 border-t border-gray-200 flex gap-2">
                <button
                  onClick={() => {
                    const updatedStructure = structure.map((b) =>
                      b.id === block.id
                        ? { ...b, children: editDraft.children }
                        : b,
                    );
                    setStructure(updatedStructure);
                    setEditingBlockId(null);
                    setIsModified(true);
                  }}
                  className="px-4 py-2 text-[11px] font-bold text-white rounded-lg"
                  style={{ background: "#20243B" }}
                  type="button"
                >
                  Salvar Cláusula
                </button>
                <button
                  onClick={() => setEditingBlockId(null)}
                  className="px-4 py-2 text-[11px] font-bold border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100"
                  type="button"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="clause-body mt-2">
            {numberedChildren.map((ch) => {
              const childVisible = isDeclarationChildVisible(ch, reasonCodes);
              const resolvedContent = resolveText(
                ch.content || "",
                placeholderVars,
              );
              const lvl = normalizeClauseLevel(ch.level);
              return (
                <div
                  key={ch.id}
                  className={`sub-clause-item has-manual-number ${!childVisible && mode === "auditoria" ? "opacity-40 line-through" : ""}`}
                  style={{
                    "--subclause-indent":
                      lvl > 0
                        ? `calc(${lvl} * (var(--clause-num-col) + var(--clause-col-gap)))`
                        : "0",
                    marginLeft: 0,
                  }}
                >
                  <div className="clause-li-row">
                    <span className="shrink-0 font-bold text-[11pt]">
                      {ch.displayNum}
                    </span>
                    <span
                      className="text-justify leading-7 text-[11.5pt] text-gray-800"
                      dangerouslySetInnerHTML={{ __html: resolvedContent }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderQuickGuide = (forPrint = false) => (
    <div
      id={forPrint ? "guia-rapido-print" : "guia-rapido"}
      className={`quadro-resumo-section mb-8${forPrint ? " print-quick-nav-page" : ""}`}
    >
      <h2 className="text-center font-bold text-sm mb-4 uppercase relative">
        Guia Rápido do Documento
      </h2>
      <div className="quadro-resumo-grid">
        {clauseBlocks.map((block, idx) => {
          const blockTitle = resolveText(block.title, placeholderVars);
          return (
            <a
              key={block.id}
              href={
                forPrint ? `#block-${block.id}` : `#decl-block-${block.id}`
              }
              onClick={
                forPrint
                  ? undefined
                  : (e) => {
                      e.preventDefault();
                      scrollToBlock(block.id);
                    }
              }
              className="quadro-resumo-card group relative"
            >
              <span
                className="ghost-marker"
                style={{ top: 0, left: 0 }}
                aria-hidden="true"
              >{`RDA_TL_BTN_${block.id}`}</span>
              <span
                className="ghost-marker"
                style={{ bottom: 0, right: 0 }}
                aria-hidden="true"
              >{`RDA_BR_BTN_${block.id}`}</span>
              <span className="quadro-resumo-icon" aria-hidden="true">
                <i
                  className={`fas ${block.icon || "fa-file-alt"}`}
                  aria-hidden="true"
                />
              </span>
              <div className="quadro-resumo-content">
                <h3 className="quadro-resumo-title-group">
                  <span className="quadro-resumo-clause-number">
                    Cláusula {idx + 1}ª
                  </span>
                  <span className="quadro-resumo-label" title={blockTitle}>
                    {blockTitle}
                  </span>
                </h3>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );

  // Espaçamento (mb-4/py-3/mb-2/mb-1) e vão de assinatura (1,6cm) são
  // canônicos e idênticos nos 5 documentos de fechamento (Contrato,
  // Procuração, Declaração, Termo de Renúncia, Termo de Revogação) — não
  // são valores arbitrários. Ver CONSTITUICAO_REGRAS.SH Art. 384-386 antes
  // de alterar.
  const renderDeclarationClosing = () => (
    <div className="mt-12 mb-4">
      <p className="text-justify leading-7 text-[11.5pt] text-gray-800 mb-4">
        Nessa condição, {decArtigoUC}{" "}
        <strong>DECLARANTE REQUER o benefício da GRATUIDADE DA JUSTIÇA</strong>{" "}
        nos termos da legislação vigente.
      </p>
      <p className="text-justify leading-7 text-[11.5pt] text-gray-800 mb-4">
        E, por corresponder à cristalina expressão da verdade legal e de sua
        livre vontade, {decArtigo} <strong>DECLARANTE</strong> firma a presente
        declaração.
      </p>
      <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-[10.5pt] leading-6 text-amber-950 text-justify mb-4">
        <strong>
          Nota de Validade Híbrida (art. 654, § 1º, da Lei n. 10.406/2002 —
          Código Civil Brasileiro):
        </strong>{" "}
        Na hipótese de assinatura eletrônica remota, o local de emissão, a data
        e a hora oficiais do ato coincidem de forma inalterável com os dados de
        geolocalização, IP e Carimbo do Tempo registrados no relatório de
        auditoria da plataforma certificadora anexa, suprindo plenamente o
        preenchimento manual dos campos. Em caso de assinatura física
        presencial, inclusive no interior de estabelecimentos prisionais ou
        hospitalares, o local exato e a data deverão ser preenchidos de próprio
        punho {decPelo} <strong>DECLARANTE</strong> ou por seu patrono nos
        campos abaixo.
      </div>
      <div className="mb-4 rounded-xl border border-dashed border-stone-300 bg-white px-4 py-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-2">
          Local e Data de Emissão
        </p>
        <p className="text-[10.5pt] italic leading-6 text-stone-400 mb-1">
          (apenas para assinatura física presencial)
        </p>
        <p className="text-[11.5pt] leading-7 text-gray-800 break-words">
          <span className="inline-block min-w-[220px] border-b border-stone-400 align-baseline">
            &nbsp;
          </span>
          ,{" "}
          <span className="inline-block min-w-[42px] text-center border-b border-stone-400 align-baseline">
            &nbsp;
          </span>{" "}
          de{" "}
          <span className="inline-block min-w-[170px] border-b border-stone-400 align-baseline">
            &nbsp;
          </span>{" "}
          de{" "}
          <span className="inline-flex min-w-[76px] border-b border-stone-400 align-baseline justify-start">
            <span className="pl-1">20</span>
          </span>
          .
        </p>
      </div>
    </div>
  );

  const renderDeclarantSignature = () => (
    <div className="flex justify-center">
      <div className="w-full max-w-md text-center border-t border-black pt-2">
        <span className="font-bold">{signerName}</span>
        <br />
        <span className="text-[11px]">{signerCpf} — DECLARANTE</span>
        {signerSub ? (
          <>
            <br />
            <span className="text-[11px]">{signerSub}</span>
          </>
        ) : null}
      </div>
    </div>
  );

  // ── derived data for DocumentShell right panel ───────────────────────────
  const rightPanelItems = clauseBlocks.map((block) => ({
    id: block.id,
    label: resolveText(block.title, placeholderVars).toUpperCase(),
    draggable: true,
    isActive: editingBlockId === block.id,
  }));

  // ── action bar JSX (extracted so render stays clean) ─────────────────────
  const actionBarJsx = (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={() => {
            if (!clientData) {
              setError("Importe um cliente do CRM antes de salvar.");
              return;
            }
            setDialogState({
              open: true,
              title: "Salvar Declaração (HTML)",
              message:
                "Esta ação irá salvar a declaração em HTML no prontuário interno para edição futura. O documento NÃO será enviado ao portal do cliente. Deseja continuar?",
              mode: "confirm",
              onConfirm: handleSaveHtml,
            });
          }}
          className="px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-violet-200 bg-white text-violet-700 hover:bg-violet-50 transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
          type="button"
          aria-disabled={isPublishing}
        >
          <i className="fas fa-save"></i>{" "}
          {isSaving ? "Salvando..." : "Salvar HTML"}
        </button>
        <button
          onClick={() => {
            if (!editingPublicationMeta?.documentId) {
              setDialogState({
                open: true,
                title: "Salve o HTML primeiro",
                message:
                  "A declaração precisa ser salva em HTML antes de ser publicada em PDF.",
                mode: "info",
                onConfirm: null,
              });
              return;
            }
            setDialogState({
              open: true,
              title: "Publicar Declaração em PDF",
              message:
                "Esta ação irá publicar a declaração em PDF no Firebase Storage e torná-la visível no portal do cliente para assinatura. Deseja continuar?",
              mode: "confirm",
              onConfirm: handlePublishPdf,
            });
          }}
          className={`px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border flex items-center justify-center gap-1.5 leading-none shadow-sm transition-colors ${editingPublicationMeta?.documentId ? "border-rose-200 bg-white text-rose-700 hover:bg-rose-50" : "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"}`}
          type="button"
          aria-disabled={isPublishing || !editingPublicationMeta?.documentId}
        >
          <i className="fas fa-file-pdf" />{" "}
          {isPublishing ? "Publicando..." : "Publicar PDF"}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-stretch">
        <div className="relative z-[320]">
          <button
            ref={advancedBtnRef}
            onClick={openAdvancedMenu}
            className="w-full px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap shadow-sm flex justify-center gap-1.5 transition-colors items-center no-print border bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100 leading-none"
            type="button"
            aria-expanded={showAdvancedActions}
          >
            <i className="fas fa-cog"></i> Avançado{" "}
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${showAdvancedActions ? "rotate-180" : ""}`}
            />
          </button>
          {showAdvancedActions &&
            createPortal(
              <div
                ref={advancedMenuRef}
                className="fixed w-64 bg-white border border-gray-200 shadow-2xl rounded-lg overflow-hidden z-[1200] flex flex-col"
                style={{
                  top: advancedMenuPos.top,
                  left: advancedMenuPos.left,
                  transform: "translateY(-100%)",
                }}
              >
                <button
                  onClick={() => {
                    setShowAdvancedActions(false);
                    handleReset();
                  }}
                  className={`text-left px-4 py-3 text-xs font-bold w-full transition-colors ${isModified ? "text-orange-700 bg-orange-50 hover:bg-orange-100" : "text-gray-700 hover:bg-gray-50"}`}
                  type="button"
                >
                  Limpar Draft
                </button>
                <button
                  onClick={() => {
                    setShowAdvancedActions(false);
                    handleBackupJson();
                  }}
                  className="text-left px-4 py-3 hover:bg-emerald-50 text-xs font-bold text-emerald-700 w-full transition-colors"
                  title="Baixar uma cópia de segurança da declaração para o seu computador"
                  type="button"
                >
                  Backup Seguro (JSON)
                </button>
                <button
                  onClick={() => {
                    setShowAdvancedActions(false);
                    handleRestoreBackup();
                  }}
                  className="text-left px-4 py-3 hover:bg-amber-50 text-xs font-bold text-amber-700 w-full transition-colors"
                  title="Restaurar declaração a partir de um arquivo JSON salvo"
                  type="button"
                >
                  Restaurar Backup
                </button>
                <button
                  onClick={() => {
                    setShowAdvancedActions(false);
                    handleRevincularCrm();
                  }}
                  className="text-left px-4 py-3 hover:bg-sky-50 text-xs font-bold text-sky-700 w-full transition-colors"
                  title="Recarrega do CRM a qualificação atual do declarante"
                  type="button"
                >
                  Revincular do CRM
                </button>
                <button
                  onClick={() => {
                    setShowAdvancedActions(false);
                    void handleSyncCloud();
                  }}
                  className="text-left px-4 py-3 hover:bg-green-50 text-xs font-bold text-green-700 w-full transition-colors border-b border-green-100"
                  title="Limpa o cache local e força o download do template atualizado da nuvem"
                  type="button"
                >
                  ☁️ Sincronizar com a Nuvem
                </button>
                <button
                  onClick={() => {
                    setShowAdvancedActions(false);
                    void handleDownloadCloudMaster();
                  }}
                  className="text-left px-4 py-3 hover:bg-sky-50 text-xs font-bold text-sky-700 w-full transition-colors"
                  title="Baixar o mestre atualmente salvo na nuvem como JSON, sem publicar nada"
                  type="button"
                >
                  Baixar Mestre da Nuvem
                </button>
                <button
                  onClick={() => {
                    setShowAdvancedActions(false);
                    handleSaveAsMaster();
                  }}
                  className="text-left px-4 py-3 hover:bg-rose-50 text-xs font-bold text-rose-700 w-full transition-colors"
                  title="Sobrescreve o mestre de declarações na nuvem com a versão atual"
                  type="button"
                >
                  Publicar Mestre
                </button>
                <button
                  onClick={() => {
                    setShowAdvancedActions(false);
                    handleImportMD();
                  }}
                  className="text-left px-4 py-3 hover:bg-emerald-50 text-xs font-bold text-emerald-700 w-full transition-colors"
                  type="button"
                >
                  Upload da Revisão (.MD)
                </button>
                <button
                  onClick={() => {
                    setShowAdvancedActions(false);
                    handleExportMD();
                  }}
                  className="text-left px-4 py-3 hover:bg-indigo-50 text-xs font-bold text-indigo-700 w-full transition-colors"
                  type="button"
                >
                  Baixar para Revisão (.MD)
                </button>
                {editingPublicationMeta?.documentId && (
                  <button
                    onClick={() => {
                      setShowAdvancedActions(false);
                      window.open(
                        `https://console.firebase.google.com/project/${appId}/firestore/data/artifacts/${appId}/public/data/documents/${editingPublicationMeta.documentId}`,
                        "_blank",
                      );
                    }}
                    className="text-left px-4 py-3 text-xs font-bold text-sky-700 hover:bg-sky-50 w-full transition-colors"
                    type="button"
                  >
                    Ver no Firestore
                  </button>
                )}
                {editingPublicationMeta?.documentId && (
                  <button
                    onClick={() => {
                      setShowAdvancedActions(false);
                      setDialogState({
                        open: true,
                        title: "Excluir Publicação do Acervo",
                        message: `Excluir permanentemente a publicação ${editingPublicationMeta.documentId} do Firestore? Esta ação não pode ser desfeita.`,
                        mode: "confirm",
                        onConfirm: async () => {
                          try {
                            await deleteDeclarationPublication(
                              editingPublicationMeta.documentId,
                            );
                            handleReset();
                            showToast("Publicação excluída do acervo.");
                          } catch (e) {
                            setError(`Erro ao excluir: ${e?.message}`);
                          }
                        },
                      });
                    }}
                    className="text-left px-4 py-3 hover:bg-red-50 text-xs font-bold text-red-600 w-full transition-colors border-t border-red-200"
                    type="button"
                  >
                    <i className="fas fa-trash-alt mr-1.5"></i> Excluir
                    Publicação do Acervo
                  </button>
                )}
              </div>,
              document.body,
            )}
        </div>
        <button
          onClick={handlePrint}
          className="w-full px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap shadow-sm flex justify-center gap-1.5 transition-colors items-center no-print border bg-[#20243B] text-white border-[#20243B] hover:brightness-95 leading-none"
          type="button"
        >
          <i className="fas fa-print" /> PDF / Imprimir
        </button>
      </div>
    </>
  );

  // ── center panel JSX ──────────────────────────────────────────────────────
  const centerPanelJsx = (
    <div
      ref={centerPanelRef}
      className="flex-1 overflow-auto relative p-4 md:p-8 bg-gray-100 min-w-0 min-h-0 print:p-0 print:overflow-visible print:bg-white"
    >
      <div className="contract-print-area">
        <div className="screen-only-view p-8 bg-gray-100 flex justify-center">
          <div className="screen-paper">
            <div className="text-center mb-8 relative">
              <h1 className="contract-title">
                DECLARAÇÃO DE{" "}
                <span className="italic">HIPOSSUFICIÊNCIA ECONÔMICA</span>
              </h1>
            </div>
            <div className="document-content space-y-6">
              {renderQuickGuide(false)}
              {(() => {
                let clauseIdx = 0;
                return structure.map((block) => {
                  if (block.type === "clause") clauseIdx++;
                  return renderBlock(block, clauseIdx, { forPrint: false });
                });
              })()}
              <div className="avoid-page-break-inside">
                {renderDeclarationClosing()}
                <div className="mb-8" style={{ marginTop: "1.6cm" }}>
                  {renderDeclarantSignature()}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="print-only-view">
          <div className="hybrid-visual-header-fixed">
            <DocumentHeader />
          </div>
          <div className="hybrid-visual-footer-fixed">
            <DocumentFooter />
          </div>
          <table className="print-layout-table">
            <thead className="hybrid-table-header-group">
              <tr>
                <td>
                  <div className="spacer-header-ghost">&nbsp;</div>
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="document-content pt-4 pb-4">
                    <div className="text-center mb-8">
                      <h1 className="contract-title">
                        DECLARAÇÃO DE{" "}
                        <span className="italic">
                          HIPOSSUFICIÊNCIA ECONÔMICA
                        </span>
                      </h1>
                    </div>
                    {renderQuickGuide(true)}
                    {(() => {
                      let clauseIdx = 0;
                      return structure.map((block) => {
                        if (block.type === "clause") clauseIdx++;
                        return renderBlock(block, clauseIdx, {
                          forPrint: true,
                        });
                      });
                    })()}
                    <div className="avoid-page-break-inside">
                      {renderDeclarationClosing()}
                      <div className="mb-8" style={{ marginTop: "1.6cm" }}>
                        {renderDeclarantSignature()}
                      </div>
                    </div>
                    <div
                      id="contrato-legal-pronto"
                      data-status="ready"
                      style={{ display: "none" }}
                    ></div>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot className="hybrid-table-footer-group">
              <tr>
                <td>
                  <div className="spacer-footer-ghost">&nbsp;</div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <DocumentShell
        title="Motor de Declarações"
        tabs={[
          { id: "client", label: "Declarante" },
          { id: "hipossuficiencia", label: "Fundamento" },
          { id: "processo", label: "Processo" },
          { id: "clauses", label: "Cláusulas" },
        ]}
        activeTab={openSection}
        onTabChange={setOpenSection}
        mode={mode}
        onModeChange={setMode}
        actionBar={actionBarJsx}
        syncState={cloudSyncState}
        syncMeta={cloudSyncMeta}
        error={error}
        rightPanelTitle="Mapa da Declaração"
        rightPanelItems={rightPanelItems}
        onRightPanelItemClick={(item) => {
          setEditingBlockId(null);
          scrollToBlock(item.id);
        }}
        onRightPanelItemDelete={(item) => {
          setClauseOverrides((p) => {
            const n = { ...p };
            delete n[item.id];
            return n;
          });
          setIsModified(true);
        }}
        centerPanel={centerPanelJsx}
      >
        {/* ── Scrollable sidebar content ── */}
        <>
          <div
            className={`rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden${openSection === "clauses" ? " hidden" : ""}`}
          >
            <ConfigSection
              id="client"
              title={`Qualificação ${_decGender === "F" ? "da" : "do"} Declarante`}
              icon="fa-user"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              <div className="space-y-2.5">
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowImport(true)}
                    className="flex-1 bg-indigo-50 border border-indigo-200 text-indigo-700 py-1.5 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1.5 hover:bg-indigo-100 transition-colors shadow-sm"
                    type="button"
                  >
                    <Download className="w-3 h-3" /> IMPORTAR DO CRM
                  </button>
                  <button
                    onClick={() => {
                      setShowImport(true);
                    }}
                    className="bg-white border border-gray-200 text-gray-700 py-1.5 px-2.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-gray-50 transition-colors shadow-sm"
                    type="button"
                    title="Adicionar Declarante"
                  >
                    <i className="fas fa-plus text-[10px]" />
                  </button>
                </div>
                <div className="space-y-2.5">
                  <div className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-[0.04em]">
                        DECLARANTE 1
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setShowImport(true)}
                          className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100"
                          type="button"
                        >
                          Importar CRM
                        </button>
                        <button
                          onClick={() => {
                            setClientData(null);
                            setCadernoId("");
                            setCadernoTitle("");
                            setIsModified(false);
                          }}
                          className="text-[10px] font-bold text-red-600 bg-white border border-gray-200 rounded px-2 py-1 hover:bg-red-50"
                          type="button"
                          title="Remover Declarante"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2.5">
                      <div className="space-y-1 col-span-2">
                        <label className={labelBaseStyle}>Nome</label>
                        <input
                          className={
                            inputBaseStyle +
                            " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"
                          }
                          value={toTitleCasePt(clientName)}
                          readOnly
                          disabled
                        />
                      </div>
                      <div className="space-y-1">
                        <label className={labelBaseStyle}>CPF/CNPJ</label>
                        <input
                          className={
                            inputBaseStyle +
                            " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"
                          }
                          value={clientCpf}
                          readOnly
                          disabled
                        />
                      </div>
                      <div className="space-y-1">
                        <label className={labelBaseStyle}>Papel</label>
                        <select
                          className={
                            inputBaseStyle +
                            " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"
                          }
                          value="DECLARANTE"
                          disabled
                        >
                          <option value="DECLARANTE">DECLARANTE</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ConfigSection>

            {/* ── Seção: Hipossuficiência ── */}
            <ConfigSection
              id="hipossuficiencia"
              title="Tipo & Fundamento"
              icon="fa-balance-scale"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              <div className="space-y-2.5">
                <div className="space-y-1">
                  <label className={labelBaseStyle}>Tipo de Declaração</label>
                  <select
                    className={inputBaseStyle}
                    value={declType}
                    onChange={(e) => {
                      setDeclType(e.target.value);
                      setIsModified(true);
                    }}
                  >
                    {DECLARATION_TYPES.map((dt) => (
                      <option key={dt.id} value={dt.id}>
                        {dt.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-[9px] text-gray-400 mt-0.5">
                    Lei n. 13.105/2015, arts. 98-99 · CF art. 5º, LXXIV ·
                    Presunção de veracidade (§ 3º art. 99)
                  </p>
                </div>
                <div className="space-y-1">
                  <label className={labelBaseStyle}>
                    Motivo da Hipossuficiência
                  </label>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 space-y-1.5">
                    {HIPOSSUFICIENCIA_REASONS.map(({ code, label }) => {
                      const isChecked = reasonCodes.includes(code);
                      return (
                        <label
                          key={code}
                          className="flex items-start gap-2 cursor-pointer group"
                        >
                          <input
                            type="checkbox"
                            value={code}
                            checked={isChecked}
                            onChange={() => {
                              setReasonCodes((prev) =>
                                prev.includes(code)
                                  ? prev.filter((c) => c !== code)
                                  : [...prev, code],
                              );
                              setIsModified(true);
                            }}
                            className="mt-0.5 accent-[#20243B]"
                          />
                          <span className="text-[10px] text-gray-700 leading-tight group-hover:text-[#20243B]">
                            <strong className="text-[#20243B]">[{code}]</strong>{" "}
                            {label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-[9px] text-gray-400 mt-0.5">
                    Podem ser marcados vários motivos · [X] aparece no PDF · Lei
                    n. 14.181/2021
                  </p>
                </div>
              </div>
            </ConfigSection>

            {/* ── Seção: Vincular a Processo ── */}
            <ConfigSection
              id="vincular"
              title="Vincular a Processo"
              icon="fa-link"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              {clientData ? (
                <div className="space-y-1">
                  <CadernoSelector
                    clientId={clientId}
                    clientName={clientName}
                    value={cadernoId}
                    onChange={(id, title) => {
                      setCadernoId(id);
                      setCadernoTitle(title);
                    }}
                  />
                  <p className="text-[9px] text-gray-400 mt-0.5">
                    A declaração será vinculada ao caderno selecionado e
                    aparecerá no portal do cliente.
                  </p>
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">
                  Importe um cliente do CRM para vincular a um processo.
                </p>
              )}
            </ConfigSection>
          </div>

          {/* ── Painel Cláusulas (Estrutura) ── */}
          {openSection === "clauses" && (
            <div className="mt-2 space-y-1.5 pb-2">
              <p className="px-1 text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                Estrutura do Documento
              </p>
              {structure.map((block, idx) => {
                const isClause = block.type === "clause";
                const blockTitle = resolveText(block.title, placeholderVars);
                const hasOverride = clauseOverrides[block.id] !== undefined;
                const isEditing = editingBlockId === block.id;
                const isVisible = isDeclarationBlockVisible(block, reasonCodes);
                return (
                  <div
                    key={block.id}
                    className={`rounded-lg border bg-white shadow-sm overflow-hidden ${isEditing ? "border-[#C5A059]" : hasOverride ? "border-amber-300" : !isVisible ? "border-dashed border-gray-300 opacity-60" : "border-gray-200"}`}
                  >
                    <div className="flex items-center gap-2 px-3 py-2">
                      <span
                        className="shrink-0 w-5 h-5 rounded-full text-white text-[9px] font-bold flex items-center justify-center"
                        style={{
                          background: isVisible ? "#20243B" : "#9ca3af",
                        }}
                      >
                        {block.type === "text" ? "P" : idx}
                      </span>
                      <span className="flex-1 text-[10px] font-semibold text-gray-700 leading-snug truncate">
                        {blockTitle}
                      </span>
                      {!isVisible && (
                        <EyeOff className="w-3 h-3 text-gray-400" />
                      )}
                      {hasOverride && (
                        <span className="text-[8px] font-bold text-amber-600 uppercase tracking-wide">
                          Custom
                        </span>
                      )}
                      <button
                        onClick={() => scrollToBlock(block.id)}
                        title="Ir para este bloco no documento"
                        className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-[#20243B]"
                        type="button"
                      >
                        <i className="fas fa-crosshairs text-[9px]" />
                      </button>
                      <button
                        onClick={() => {
                          if (isEditing) {
                            setEditingBlockId(null);
                          } else {
                            setEditingBlockId(block.id);
                            if (block.type === "text") {
                              setEditDraft({
                                content:
                                  clauseOverrides[block.id] ||
                                  block.content ||
                                  "",
                              });
                            } else {
                              setEditDraft({
                                children: JSON.parse(
                                  JSON.stringify(block.children || []),
                                ),
                              });
                            }
                          }
                        }}
                        title={isEditing ? "Fechar editor" : "Editar"}
                        className={`p-1 rounded text-[9px] transition-colors ${isEditing ? "bg-[#C5A059] text-white" : "hover:bg-gray-100 text-gray-400 hover:text-[#20243B]"}`}
                        type="button"
                      >
                        <i
                          className={`fas ${isEditing ? "fa-times" : "fa-pencil-alt"}`}
                        />
                      </button>
                    </div>
                    {isEditing && isClause && (
                      <div className="border-t border-amber-200 p-2 space-y-2 bg-amber-50/60 max-h-[300px] overflow-y-auto">
                        <p className="text-[9px] text-gray-500">
                          {(block.children || []).length} sub-cláusulas · Edite
                          no painel central (modo Estrutura)
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      </DocumentShell>

      {/* ══ Dialog ══ */}
      {dialogState.open ? (
        <div className="fixed inset-0 z-[700] flex items-center justify-center p-4 no-print">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              const fn = dialogState.onCancel;
              setDialogState({
                open: false,
                title: "",
                message: "",
                mode: "confirm",
                onConfirm: null,
                onCancel: null,
              });
              if (typeof fn === "function") fn();
            }}
          />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 p-5">
            <div className="text-sm font-bold text-[#20243B]">
              {dialogState.title || "Confirmar"}
            </div>
            <div className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">
              {dialogState.message}
            </div>
            <div className="mt-5">
              {dialogState.mode === "confirm" ? (
                <SaveCancelBar
                  onCancel={() => {
                    const fn = dialogState.onCancel;
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    });
                    if (typeof fn === "function") fn();
                  }}
                  onSave={() => {
                    const fn = dialogState.onConfirm;
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    });
                    if (typeof fn === "function") fn();
                  }}
                  cancelLabel="Cancelar"
                  saveLabel="Confirmar"
                />
              ) : (
                <SaveCancelBar
                  onCancel={() =>
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    })
                  }
                  onSave={() =>
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    })
                  }
                  cancelLabel=""
                  saveLabel="OK"
                  disabledCancel={true}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* ══ Toast ══ */}
      {showImport &&
        createPortal(
          <CrmClientPickerModal
            isOpen={showImport}
            onClose={() => setShowImport(false)}
            onSelect={handleImportClient}
            db={db}
            appId={appId}
            title="Base de Clientes (CRM)"
          />,
          document.body,
        )}

      {toastMsg && (
        <div className="fixed bottom-6 right-6 z-[1400] bg-[#20243B] text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-2xl border border-[#C5A059]/30 flex items-center gap-2 no-print">
          <CheckCircle className="w-4 h-4 text-[#C5A059]" />
          {toastMsg}
        </div>
      )}
    </>
  );
}

export default DeclarationFactory;
